import { useMemo, useState } from "react";
import { Product } from "../types";
import { Button } from "@/components/ui/button";
import { CartObj } from "../cart/cart";

interface VariantSelectorProps {
  product: Product;
  initialSelectedOptions: Record<string, string>;
}

export default function VariantSelector({ product, initialSelectedOptions }: VariantSelectorProps) {
  const variantOptions = useMemo(() => {
    return product.variants.reduce(
      (acc, variant) => {
        variant.selectedOptions.forEach((option) => {
          if (!acc[option.name]) acc[option.name] = new Set();
          acc[option.name].add(option.value);
        });
        return acc;
      },
      {} as Record<string, Set<string>>,
    );
  }, [product]);

  const [selectedOptions, setSelectedOptions] = useState(initialSelectedOptions);

  const availableVariants = useMemo(() => {
    return product.variants.filter(
      (variant) =>
        Object.entries(selectedOptions).every(([key, value]) =>
          variant.selectedOptions.find(
            (option) => option.name === key && option.value === value,
          ),
        ) && variant.availableForSale,
    );
  }, [product, selectedOptions]);

  const isOptionAvailable = (optionName: string, optionValue: string) => {
    const tempSelected = { ...selectedOptions, [optionName]: optionValue };

    return product.variants.some(
      (variant) =>
        Object.entries(tempSelected).every(([key, value]) =>
          variant.selectedOptions.find(
            (option) => option.name === key && option.value === value,
          ),
        ) && variant.availableForSale,
    );
  };

  const handleOptionChange = (optionName: string, optionValue: string) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [optionName]: optionValue,
    }));
  };

  const selectedVariant = availableVariants.find((variant) =>
    Object.entries(selectedOptions).every(([key, value]) =>
      variant.selectedOptions.find(
        (option) => option.name === key && option.value === value,
      ),
    ),
  );

  const handleAddToCart = async () => {
    const cart = new CartObj("shopify");
    const res = await cart.addToCart({
      variantId: selectedVariant?.id!,
    });
    console.log({ res });
    console.log("added to cart");
  };

  return (
    <div className="hp-mx-auto hp-max-w-2xl hp-rounded-lg hp-bg-white hp-p-2 hp-shadow-md">
      <div className="hp-flex hp-flex-col hp-gap-6">
        <div className="hp-space-y-4">
          <div className="mx-auto aspect-square w-full max-w-[300px] overflow-hidden rounded-md bg-gray-100">
            <img
              src={
                selectedVariant?.image?.url ??
                product.featuredImage?.url ??
                product.image
              }
              alt={product.title}
              className="h-full w-full transform object-cover transition-all duration-300 ease-in-out hover:scale-105"
            />
          </div>

          <h2 className="hp-line-clamp-2 hp-text-2xl hp-font-bold">{product.title}</h2>

          {Object.keys(variantOptions).map((optionName) => (
            <div key={optionName}>
              <div className="hp-mb-1 hp-block hp-text-lg hp-font-bold">{optionName}</div>
              <div className="hp-flex hp-flex-wrap hp-gap-2">
                {[...variantOptions[optionName]].map((optionValue) => {
                  const isAvailable = isOptionAvailable(
                    optionName,
                    optionValue,
                  );
                  return (
                    <Button
                      key={optionValue}
                      onClick={() =>
                        handleOptionChange(optionName, optionValue)
                      }
                      className={`rounded-md text-sm font-medium transition-colors duration-200 ${
                        selectedOptions[optionName] === optionValue
                          ? "bg-primary text-primary-foreground"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                      disabled={!isAvailable}
                    >
                      {optionValue}
                    </Button>
                  );
                })}
              </div>
            </div>
          ))}

          <Button
            onClick={handleAddToCart}
            className="w-full transform py-2 text-sm transition-all duration-200 hover:scale-105"
            disabled={!selectedVariant}
          >
            Add to Cart
          </Button>
        </div>
      </div>
    </div>
  );
}
